// Hideo - the data folder. On the website it is the legacy one (File System Access API, Chromium only), whose UI was
// removed on 5/10/2026 (Google Drive replaces it): at start-up initFolder silently re-attaches a folder the browser has
// already authorised (its handle is in IndexedDB "creatorOS_handles", store "h", key "dir"); otherwise nothing happens.
// In the desktop app (src/features/desktop.ts) it is a folder of the computer chosen in Settings › App (its path in
// creatorOS_desktopFolder), read and written through Tauri (nativeFolder.ts). While it is in use the active profile
// is written there (500 ms after the last change, with a daily copy in backup/) instead of the browser, and the profiles
// list has a copy there (creatoros-profiles.json, merged by updatedAt).
// Ported from the legacy core/data.js (verbatim copy in tests/fixtures/legacy/data.js, golden tests in tests/saving*.test.ts).
import { t } from '../../i18n'
import { backupFile, cleanProfile, dataFile, lsKey, PFNAME, validProfileId, writeLocal } from '../../data/profiles'
import { useDataStore } from '../../stores/data'
import { usePickerStore } from '../../stores/picker'
import { uiConfirm } from '../../stores/notices'
import { fileStamp, refreshWarn, setSync, syncOf } from './sync'
import { autoSyncOn, syncTargets } from '../cloud/autosync'
import { app } from '../app'
import { desktopFolderPath, isDesktop, pickDesktopFolder, setDesktopFolderPath } from '../desktop'
import { nativeFolder } from './nativeFolder'

/** A folder handle with the permission calls of the File System Access API */
export interface FolderHandle extends FileSystemDirectoryHandle {
  queryPermission(o: { mode: 'readwrite' }): Promise<string>
}
/** State of the legacy data folder: its handle, whether it is in use (ok), the pending write, the day of the last backup per profile */
export const FOLDER = {
  dirHandle: null as FolderHandle | null | undefined,
  ok: false,
  writeTimer: null as ReturnType<typeof setTimeout> | null,
  lastBackup: {} as Record<string, string>
}

/** Profiles list: this browser (localStorage) + a copy in the legacy data folder */
export function saveProfiles(): void {
  const store = useDataStore()
  store.saveProfiles()
  if (FOLDER.ok) writeFile(FOLDER.dirHandle as FolderHandle, PFNAME, JSON.stringify(store.profiles.list, null, 1)).catch(e => console.error(e))
}
/** A JSON file of the legacy data folder, or null */
export async function readFolderJson(name: string): Promise<unknown> {
  try { return JSON.parse(await (await (await (FOLDER.dirHandle as FolderHandle).getFileHandle(name)).getFile()).text()) } catch { return null }
}
// Merge the folder's profiles (e.g. created or edited on another PC) into this browser's:
// new profiles are added, existing ones are updated if the folder copy is newer
async function mergeFolderProfiles(): Promise<void> {
  const list = await readFolderJson(PFNAME), PROFILES = useDataStore().profiles
  if (!Array.isArray(list)) return
  list.forEach(x => {
    if (!x || !validProfileId(x.id)) return
    const p = cleanProfile(x), local = PROFILES.list.find(l => l.id === p.id)
    if (!local) PROFILES.list.push(p); else if ((p.updatedAt as number) > (local.updatedAt as number)) Object.assign(local, p)
  })
}
/** IndexedDB "creatorOS_handles" (store "h"): one request in a transaction, resolved with its result when it completes */
function idb<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return new Promise((res, rej) => {
    const r = indexedDB.open('creatorOS_handles', 1)
    r.onupgradeneeded = () => r.result.createObjectStore('h')
    r.onsuccess = () => { const tx = r.result.transaction('h', mode), q = fn(tx.objectStore('h')); tx.oncomplete = () => res(q.result); tx.onerror = () => rej(tx.error) }
    r.onerror = () => rej(r.error)
  })
}
export async function writeFile(dir: FileSystemDirectoryHandle, name: string, text: string): Promise<void> { const fh = await dir.getFileHandle(name, { create: true }); const w = await fh.createWritable(); await w.write(text); await w.close() }
/** Writes the active profile's data into the legacy data folder now (and the day's backup, once a day). If it fails the
 *  folder is no longer used (the data goes back to the browser at the next change) */
export async function writeFolder(): Promise<void> {
  FOLDER.writeTimer = null
  try {
    // Date and device go in the file too: if the folder is inside Google Drive, the phone can load it directly
    const store = useDataStore(), id = store.profiles.current, stamp = fileStamp(), txt = JSON.stringify({ ...store.raw, ...stamp }, null, 1), day = stamp.savedAt.slice(0, 10)
    const dir = FOLDER.dirHandle as FolderHandle
    await writeFile(dir, dataFile(id), txt)
    if (FOLDER.lastBackup[id] !== day) { await writeFile(await dir.getDirectoryHandle('backup', { create: true }), backupFile(id, day), txt); FOLDER.lastBackup[id] = day }
    // A profile that saves online stays "to save" until the online save has it: the folder is not the online copy
    // (before 17/10/2026 the folder cleared it, so the autosave never uploaded while a folder was in use)
    setSync(id, { dirty: autoSyncOn() && syncTargets(id).length > 0 ? !!syncOf(id).dirty : false, file: { ...stamp, kind: 'cartella' } })
  } catch (e) { console.error(e); FOLDER.ok = false; refreshWarn() }
}
async function adoptFolder(): Promise<void> {
  const store = useDataStore()
  store.getData()
  await mergeFolderProfiles()
  const key = lsKey(store.profiles.current), existing = await readFolderJson(dataFile(store.profiles.current)) as { videos?: unknown } | null
  const hasLocal = !!localStorage.getItem(key)
  if (existing && existing.videos) {
    if (!hasLocal || await uiConfirm(t('The folder already contains data for the profile "{name}". Which do you want to use?', { name: store.currentProfile()!.name }), { ok: t("The folder's"), cancel: t('The current ones') })) { store.setData(existing); app.renderAll() }
  }
  FOLDER.ok = true; await writeFolder()
  if (FOLDER.ok) { try { localStorage.removeItem(key) } catch { /* blocked storage */ } saveProfiles() }
  refreshWarn(); usePickerStore().renderUi()
}
/** At start-up (app.boot, src/features/app.ts): re-attaches the legacy data folder if the browser still allows it */
export async function initFolder(): Promise<void> {
  useDataStore().getData()
  // Desktop app: the folder chosen in Settings › App, if any (never the browser's legacy folder)
  if (isDesktop()) {
    const path = desktopFolderPath()
    if (path) { FOLDER.dirHandle = nativeFolder(path); try { await adoptFolder() } catch (e) { console.error(e); FOLDER.ok = false } }
    return refreshWarn()
  }
  if (!window.indexedDB) return
  // Browsers without folders (Firefox, Safari, phones) can't have one: the database is not opened (nor created) there
  if (!('showDirectoryPicker' in window)) return refreshWarn()
  try { FOLDER.dirHandle = await idb<FolderHandle>('readonly', s => s.get('dir')) } catch { /* no handle */ }
  if (FOLDER.dirHandle && await FOLDER.dirHandle.queryPermission({ mode: 'readwrite' }) === 'granted') await adoptFolder()
  refreshWarn()
}

/** Desktop app: chooses the data folder (Settings › App). From then on every profile is written there, with a daily copy
 *  in backup/; if the folder already has data for the active profile the user picks which to keep */
export async function chooseDesktopFolder(): Promise<void> {
  if (FOLDER.ok) return
  const path = await pickDesktopFolder(); if (!path) return
  FOLDER.dirHandle = nativeFolder(path)
  try { await adoptFolder() } catch (e) { console.error(e); FOLDER.ok = false; FOLDER.dirHandle = null; refreshWarn(); return }
  if (FOLDER.ok) setDesktopFolderPath(path)
}
/** Desktop app: stops using the data folder. Every profile's data goes back to the app's storage (the files stay in the
 *  folder, untouched) */
export async function leaveDesktopFolder(): Promise<void> {
  if (!FOLDER.ok) return
  const store = useDataStore()
  if (FOLDER.writeTimer) { clearTimeout(FOLDER.writeTimer); await writeFolder() }
  for (const p of store.profiles.list) {
    if (p.id === store.profiles.current) { writeLocal(p.id, store.raw); continue }
    const d = await readFolderJson(dataFile(p.id)) as Record<string, unknown> | null
    if (d) { delete d.savedAt; delete d.savedOn; writeLocal(p.id, d) }
  }
  FOLDER.ok = false; FOLDER.dirHandle = null; setDesktopFolderPath('')
  refreshWarn(); usePickerStore().renderUi()
}
/** Desktop app: path of the data folder in use ('' = none) */
export const desktopFolderInUse = (): string => (FOLDER.ok ? desktopFolderPath() : '')
